import {
  coerceBhConnectionCount,
  formatClosingValueSavings,
  type ClosingValueCostReductionRow,
} from '@act/shared/data-transfer-objects';
import SectionReveal from './SectionReveal';
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import { ReportMockFieldFrame } from '../ReportMockFieldFrame';
import type { ReportHostedSectionProps } from '../reportHostedSection.types';
import { body, CORAL, display, SEAGREEN } from './tokens';

const ROW_THEMES = {
  bh: {
    color: SEAGREEN,
    background: 'rgba(129,205,195,0.15)',
    border: 'rgba(129,205,195,0.3)',
  },
  pcp: {
    color: CORAL,
    background: 'rgba(255,123,101,0.12)',
    border: 'rgba(255,123,101,0.3)',
  },
} as const;

const DEFAULT_SUMMARY =
  'Removing barriers and engaging members in care drives total cost of care reduction. Per peer-reviewed research, the average annual savings for initiation into BH outpatient care is $2,000 per member, and the average annual savings for a first PCP visit is $4,000 per member. Applied to Reema\'s engaged population, the projected savings are below.';

const FlowArrow = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    aria-hidden="true"
  >
    <svg width="38" height="14" viewBox="0 0 1000 103" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1000.0,48.2 C999.9,46.3 998.8,44.6 997.2,43.7 L925.0,0.8 C922.2,-0.8 918.6,0.0 917.0,2.7 C915.3,5.4 916.2,9.0 918.9,10.6 L974.9,43.8 L5.7,55.2 C2.5,55.2 -0.1,57.9 0.0,61.0 C0.0,62.9 1.1,64.6 2.6,65.6 C3.6,66.3 4.7,66.6 6.0,66.6 L976.5,55.2 L928.7,92.9 C926.1,94.8 925.8,98.4 927.8,100.9 C928.2,101.4 928.6,101.7 929.1,102.1 C931.2,103.4 934.0,103.3 936.0,101.7 L997.8,53.0 C999.3,51.8 1000.1,50.0 1000.0,48.2 Z"
        fill="var(--reema-coral-100)"
      />
    </svg>
  </div>
);

interface CostTileProps {
  label: string;
  value: string;
  theme: (typeof ROW_THEMES)[keyof typeof ROW_THEMES];
}

/**
 * Renders one metric tile in the cost-reduction equation row.
 * @param props - Label, formatted value, and row theme colors.
 */
const CostTile = ({ label, value, theme }: CostTileProps) => (
  <div
    style={{
      background: theme.background,
      border: `1px solid ${theme.border}`,
      borderRadius: 14,
      padding: '1.5rem 1.25rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      minHeight: 150,
    }}
  >
    <div
      style={{
        fontFamily: body,
        fontWeight: 700,
        fontSize: '0.7rem',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.6)',
        lineHeight: 1.4,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontFamily: display,
        fontWeight: 600,
        fontSize: '3.5rem',
        lineHeight: 1,
        color: theme.color,
        marginTop: '0.75rem',
      }}
    >
      {value}
    </div>
  </div>
);

const readRows = (data: Record<string, unknown>): ClosingValueCostReductionRow[] => {
  if (!Array.isArray(data.costReductionRows)) {
    return [];
  }
  return (data.costReductionRows as ClosingValueCostReductionRow[]).map((row, index) => ({
    ...row,
    theme: row.theme ?? (index === 0 ? 'bh' : 'pcp'),
  }));
};

/**
 * The Bottom Line: BH/PCP cost-reduction math on a midnight Studio layout.
 * @param props - Resolved section snapshot and customer context.
 */
const ClosingValue = ({ data }: ReportHostedSectionProps) => {
  const rows = readRows(data);
  const total = coerceBhConnectionCount(data.total);
  const summary =
    typeof data.summary === 'string' ? data.summary : DEFAULT_SUMMARY;
  const combinedLabel =
    typeof data.combinedLabel === 'string'
      ? data.combinedLabel
      : 'Combined projected savings from connection to care alone';

  const summaryParts = summary.split(/(\$2,000|\$4,000|peer-reviewed research)/g);

  return (
    <HostedReportSectionLayout
      sectionDomId="closing-value"
      className="section--midnight report-hosted-section-body"
      style={{ background: 'var(--reema-midnight-100)', fontFamily: body }}
      innerClassName="container"
      banner={null}
    >
      <SectionReveal>
        <div style={{ marginBottom: '4rem' }}>
          <span
            className="overline"
            style={{
              color: 'var(--reema-coral-100)',
              display: 'block',
              marginBottom: '1rem',
            }}
          >
            THE BOTTOM LINE
          </span>
          <h2
            className="heading-xl"
            style={{
              color: 'var(--white)',
              maxWidth: '48rem',
              marginBottom: '1.5rem',
              fontFamily: display,
              fontWeight: 600,
              lineHeight: 1.12,
              letterSpacing: '-0.01em',
            }}
          >
            Removing barriers and engaging members in care drives total cost of care
            reduction.
          </h2>
          <p
            style={{
              fontFamily: body,
              fontSize: '1.125rem',
              lineHeight: 1.55,
              color: 'var(--reema-sand-100)',
              maxWidth: '36rem',
              margin: 0,
            }}
          >
            {summaryParts.map((part, index) =>
              part === 'peer-reviewed research' ||
              part === '$2,000' ||
              part === '$4,000' ? (
                <span key={`${part}-${index}`} style={{ color: CORAL }}>
                  {part}
                </span>
              ) : (
                <span key={`${part}-${index}`}>{part}</span>
              ),
            )}
          </p>
        </div>

        <ReportMockFieldFrame data={data} field="costReductionRows">
          <div className="space-y-4">
            {rows.map((row, index) => {
              const theme = ROW_THEMES[row.theme] ?? ROW_THEMES.bh;
              return (
                <div
                  key={`${row.leftLabel}-${index}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 60px 1fr 60px 1fr',
                    gap: '1rem',
                    alignItems: 'stretch',
                  }}
                >
                  <CostTile
                    label={row.leftLabel}
                    value={row.leftValue.toLocaleString()}
                    theme={theme}
                  />
                  <FlowArrow />
                  <CostTile
                    label={row.rightLabel}
                    value={row.rightValue.toLocaleString()}
                    theme={theme}
                  />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: display,
                      fontWeight: 500,
                      fontSize: '1.75rem',
                      color: 'rgba(255,255,255,0.5)',
                    }}
                  >
                    =
                  </div>
                  <CostTile
                    label={row.resultLabel}
                    value={formatClosingValueSavings(row.resultValue)}
                    theme={theme}
                  />
                </div>
              );
            })}
          </div>
        </ReportMockFieldFrame>

        <ReportMockFieldFrame data={data} field="total">
          <p
            style={{
              fontFamily: body,
              fontSize: '1rem',
              color: 'rgba(255,255,255,0.8)',
              marginTop: '2rem',
              marginBottom: 0,
              textAlign: 'center',
            }}
          >
            {combinedLabel}:{' '}
            <strong style={{ color: CORAL, fontWeight: 600 }}>
              {formatClosingValueSavings(total)}
            </strong>
          </p>
        </ReportMockFieldFrame>
      </SectionReveal>
    </HostedReportSectionLayout>
  );
};

export default ClosingValue;
